/**
 * useServiceSEO.js
 * Extension de useSEO pour injecter le Schema.org "Service"
 * sur les pages de détail de chaque soin.
 *
 * Usage :
 *   useServiceSEO({ service, categoryName })
 */
import { useEffect } from 'react';
import useSEO from './UseSeo';

const BASE_URL  = 'https://www.alaia-cristal-bienetre.fr/';  // ← adapter
const SITE_NAME = 'Alaïa Cristal Bien-être';

const setScript = (id, json) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('id', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(json);
};

const removeScript = (id) => {
  const el = document.getElementById(id);
  if (el) el.remove();
};

const useServiceSEO = ({ service, categoryName }) => {
  /* ── Meta title & description de la page soin ── */
  const title = service
    ? `${service.name} — ${categoryName || 'Soin'} à Bessines`
    : null;

  const description = service
    ? `${service.description?.slice(0, 155)}… Réservez votre ${service.name} chez ${SITE_NAME} à Bessines (79), au cœur du Spa Eclipsea.`
    : null;

  useSEO({ title, description, path: '/', type: 'website' });

  /* ── Schema.org Service ── */
  useEffect(() => {
    if (!service) {
      removeScript('sd-service');
      return;
    }

    /* Nettoyage du prix pour Schema : "90€-120€" → "90" */
    const rawPrice  = service.price?.replace(/[€\s]/g, '').split('-')[0] || '';
    const priceNum  = parseFloat(rawPrice) || undefined;

    setScript('sd-service', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: service.name,
      description: service.description,
      serviceType: categoryName || 'Massage bien-être',
      provider: {
        '@type': 'HealthAndBeautyBusiness',
        name: SITE_NAME,
        url: BASE_URL,
        address: {
          '@type': 'PostalAddress',
          streetAddress: '21 Rue des Charmes',
          addressLocality: 'Bessines',
          postalCode: '79000',
          addressCountry: 'FR',
        },
      },
      areaServed: {
        '@type': 'Place',
        name: 'Bessines, Deux-Sèvres, Nouvelle-Aquitaine',
      },
      ...(priceNum && {
        offers: {
          '@type': 'Offer',
          priceCurrency: 'EUR',
          price: priceNum,
          availability: 'https://schema.org/InStock',
          url: BASE_URL,
        },
      }),
      ...(service.duration && {
        additionalProperty: {
          '@type': 'PropertyValue',
          name: 'Durée',
          value: service.duration,
        },
      }),
    });

    return () => removeScript('sd-service');
  }, [service, categoryName]);
};

export default useServiceSEO;